import styles from 'css/src/components/agent/AIMediaResult.module.scss';

import React from 'react';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type ToolExecutionContext,
} from '../types';

import { Tool } from './Tool';

import { SuggestUpgradeTool } from 'js/src/agent/tools/sketch/SuggestUpgradeTool';
import type {
    AssetContainerModel,
    SlideBackgroundModel,
    SupportedAIMediaAspectRatios,
} from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    ErrorEnum,
    GenerativeMediaErrorCodes,
    ImageGenerationRequestModelEnum,
    VideoGenerationRequestModelEnum,
} from 'js/src/client';
import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { getUsageLimits } from 'js/src/data/usage-limit/usage-limit-selectors';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { pluralize } from 'js/src/libs/custom-utils';
import PollAsyncTask from 'js/src/libs/poll_async_task';
import { isApiErrorWithProperty, isErrorObject, OperationAbortedError } from 'js/src/types';

// Free-plan users have no animation minutes, so the backend rejects AI video
// generation with a feature-limit error. We surface the same upgrade prompt both
// proactively (before calling the API, when we already know the plan has no
// allowance) and as a fallback when the API returns the limit error anyway.
const VIDEO_UPGRADE_MESSAGE = `Error: Generating AI video clips requires a paid plan. Use the ${SuggestUpgradeTool.toolName} tool with feature "generate_ai_video_clip" to show the user that they need to upgrade.`;

export const generateAIMediaSchema = z.object({
    prompt: z
        .string()
        .describe(
            'The text prompt describing the media you want to generate. Be descriptive and specific for best results. If calling multiple times use the same descriptors to get media that match in style.',
        ),
    reference_media_id: z
        .string()
        .optional()
        .describe(
            'Optional: ID of a reference image from search results. For images: generates stylistically similar images. For videos: animates the reference image into video (image-to-video). The reference must be an image, not a video.',
        ),
    aspect_ratio: z
        .enum(VideoData.ALL_ASPECT_RATIOS)
        .optional()
        .describe(
            'Optional: Aspect ratio for generated media. Defaults to landscape. Options: landscape (16:9), square (1:1), vertical (9:16), fourfive (4:5).',
        ),
    media_type: z
        .enum(['image', 'video'])
        .optional()
        .describe(
            'Type of media to generate. "image" generates one AI image. "video" generates one 5-second AI video clip. Defaults to "image".',
        ),
});
export type AIMediaArgs = z.infer<typeof generateAIMediaSchema>;

interface MediaRepresentation {
    id: string;
    type: string;
    title: string;
    tags: string;
}

const GENERATION_TIMEOUT_MS = 30_000;
const GENERATION_TIMEOUT_REASON = 'ai-media-generation-timeout';

const SimilarImagePrompt = (prompt: string) => {
    return `Given the attached reference image, if it contains a distinct character, preserve their facial features, hairstyle, and clothing style consistently across all scenes. 
Maintain the character’s identity exactly, including unique traits such as freckles, scars, or accessories. When the prompt specifies a new environment or background, replace only the background elements while preserving the original lighting, shading, and artistic style of the character.
Do not alter the character’s pose, expression, or outfit in these cases. 
Apply style transfer from the reference image’s artistic style—such as color palette, brush strokes, and texture—consistently throughout all images and scenes. 
Regardless of scene changes, ensure the original character remains visually identical and true to the base reference.

Now generate an image of ${prompt}
`;
};

export class GenerateAIMediaTool extends Tool<typeof generateAIMediaSchema> {
    public static readonly toolName = 'generate_ai_media';

    protected override readonly schema = generateAIMediaSchema;
    protected override readonly toolName = GenerateAIMediaTool.toolName;
    protected override readonly toolDescription =
        'Generate AI media (images or videos) using a text prompt. For images: generates one AI image. For videos: generates one 5-second video clip. Can also use a reference image for style-similar images or to animate into video.';

    public override canRunAsync = true;
    public override customRenderer = AIMediaResultRenderer;
    public override customRendererMode = CustomRendererMode.INLINE_HIDDEN;

    public override getToolDisplayName(): string {
        return 'Generating AI media';
    }

    /**
     * Mirrors the backend gate on AI video generation: the plan must grant some
     * animation minutes, either an ongoing allowance or trial minutes. A user who
     * has an allowance but has used it up still passes here and falls back to the
     * API limit error. If usage limits haven't loaded yet we defer to the API
     * rather than block a paid user on stale state.
     */
    private planAllowsVideoGeneration(): boolean {
        const usageLimits = getUsageLimits(this.getState());
        if (!usageLimits?.isLoaded) {
            return true;
        }
        const hasPoolMinutes = (usageLimits.animation_seconds_limit_per_cycle ?? 0) > 0;
        const hasTrialMinutes = (usageLimits.trial_animation_seconds_limit ?? 0) > 0;
        return hasPoolMinutes || hasTrialMinutes;
    }

    public get statusIcon(): string {
        return 'auto_awesome';
    }

    public async execute(args: AIMediaArgs, context: ToolExecutionContext) {
        const { prompt, reference_media_id, aspect_ratio, media_type = 'image' } = args;

        // Validate prompt length (similar to GenerativeMediaTab)
        const MAX_CHAR_COUNT = 500;
        if (prompt.length > MAX_CHAR_COUNT) {
            return `Error: Prompt is too long (${prompt.length} characters). Maximum length is ${MAX_CHAR_COUNT} characters. Please shorten your description and focus on key visual details.`;
        }

        if (!prompt.trim()) {
            return 'Error: Prompt cannot be empty. Please provide a description of the media you want to generate.';
        }

        const mediaRegistry = MediaRegistry.getInstance();

        // Handle reference image if provided
        let assetUuid: string | undefined;
        if (reference_media_id) {
            if (!mediaRegistry.hasItem(reference_media_id)) {
                return `Error: Unknown media ${reference_media_id}`;
            }

            const mediaData = mediaRegistry.getItem(reference_media_id);
            if (!mediaData) {
                return `Error: Could not retrieve media data for ${reference_media_id}`;
            }

            // Extract UUID based on media type
            const imageUuid = mediaData.background?.image?.uuid;
            const videoUuid = mediaData.background?.video?.uuid;

            if (videoUuid && !imageUuid) {
                return `Error: Reference media ${reference_media_id} is a video. Only images can be used as reference for media generation.`;
            }

            if (!imageUuid) {
                return `Error: Could not extract asset UUID from media ${reference_media_id}`;
            }

            assetUuid = imageUuid;
        }

        if (media_type === 'video' && !this.planAllowsVideoGeneration()) {
            return VIDEO_UPGRADE_MESSAGE;
        }

        try {
            // Branch based on media type
            if (media_type === 'video') {
                return await this.generateVideo(prompt, assetUuid, aspect_ratio, mediaRegistry);
            } else {
                return await this.generateImage(prompt, assetUuid, aspect_ratio, mediaRegistry);
            }
        } catch (error) {
            let errorMessage = 'An error occurred while generating media';

            if (isErrorObject(error) && error.error instanceof OperationAbortedError) {
                return 'Media generation was cancelled';
            }

            if (
                media_type === 'video' &&
                isApiErrorWithProperty(error, 'error_code') &&
                error.body.error_code === ErrorEnum.HIT_FEATURE_USAGE_LIMIT
            ) {
                return VIDEO_UPGRADE_MESSAGE;
            }

            if (isErrorObject(error) && error.result !== undefined) {
                switch (error.result?.type) {
                    case GenerativeMediaErrorCodes.NSFW: {
                        return 'Error: This request goes against our AI terms of service. Please try a different prompt.';
                    }
                    case GenerativeMediaErrorCodes.USER_CANCELLED: {
                        return 'Media generation was cancelled';
                    }
                    case GenerativeMediaErrorCodes.NO_MEDIA_GENERATED: {
                        return 'Try a different image or a more descriptive prompt.';
                    }
                    // No default
                }
            }

            if (isApiErrorWithProperty(error, 'prompt') && Array.isArray(error.body.prompt)) {
                errorMessage = error.body.prompt[0] || errorMessage;
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }

            return `Error generating media: ${errorMessage}`;
        }
    }

    /**
     * Polls for async task result and registers the media in MediaRegistry.
     * Common logic shared between image and video generation.
     */
    private async pollAndRegisterMedia(
        taskId: string,
        taskName: string,
        mediaRegistry: MediaRegistry,
    ): Promise<string> {
        const abortController = new AbortController();
        const timeoutId = window.setTimeout(() => {
            abortController.abort(GENERATION_TIMEOUT_REASON);
        }, GENERATION_TIMEOUT_MS);

        let result: SlideBackgroundModel;
        try {
            result = (await PollAsyncTask.poll_for_result(
                taskId,
                taskName,
                undefined,
                abortController,
            )) as unknown as SlideBackgroundModel;
        } catch (error) {
            if (
                isErrorObject(error) &&
                error.error instanceof OperationAbortedError &&
                abortController.signal.reason === GENERATION_TIMEOUT_REASON
            ) {
                return `Error: Media generation timed out after ${GENERATION_TIMEOUT_MS / 1000} seconds. Please try again.`;
            }
            throw error;
        } finally {
            window.clearTimeout(timeoutId);
        }

        const assetContainer: AssetContainerModel = {
            type: AssetContainerTypes.BACKGROUND,
            audio: null,
            background: result,
            font: null,
            icon: null,
            document: null,
            folder_id: null,
            article_id: null,
            document_id: null,
        };

        const id = mediaRegistry.addItem(assetContainer);
        const representation = mediaRegistry.getRepresentation(id);
        return JSON.stringify([representation], null, 2);
    }

    private async generateImage(
        prompt: string,
        assetUuid: string | undefined,
        aspect_ratio: AspectRatio | undefined,
        mediaRegistry: MediaRegistry,
    ): Promise<string> {
        // Use the free "basic" models by default so free-plan users aren't blocked
        const model = assetUuid
            ? ImageGenerationRequestModelEnum.FAL_AI_FLUX_KONTEXT_DEV
            : ImageGenerationRequestModelEnum.FAL_AI_FLUX_SCHNELL;

        // Convert aspect ratio to API format (e.g., "16:9")
        const aspectRatioValue = aspect_ratio
            ? (VideoData.getAspectRatioDisplayRatio(aspect_ratio) as SupportedAIMediaAspectRatios)
            : null;

        const response = await ApiService.imageGenerationCreate({
            body: {
                prompt: assetUuid ? SimilarImagePrompt(prompt.trim()) : prompt.trim(),
                model,
                aspect_ratio: aspectRatioValue,
                asset_uuids: assetUuid ? [assetUuid] : [],
            },
        });

        return this.pollAndRegisterMedia(response.data.task_id, 'generate_image', mediaRegistry);
    }

    private async generateVideo(
        prompt: string,
        assetUuid: string | undefined,
        aspect_ratio: AspectRatio | undefined,
        mediaRegistry: MediaRegistry,
    ): Promise<string> {
        // Determine model based on whether we have a reference image
        const model = assetUuid
            ? VideoGenerationRequestModelEnum.FAL_AI_WAN_V2_2_A14B_IMAGE_TO_VIDEO_TURBO
            : VideoGenerationRequestModelEnum.FAL_AI_WAN_V2_2_A14B_TEXT_TO_VIDEO_TURBO;

        // WAN 2.2 Turbo has fixed 5 second duration
        const duration = 5;

        // Convert aspect ratio to API format (e.g., "16:9")
        const aspectRatioValue = aspect_ratio
            ? VideoData.getAspectRatioDisplayRatio(aspect_ratio)
            : '16:9';

        const response = await ApiService.videoGenerationCreate({
            body: {
                prompt: prompt.trim(),
                model,
                aspect_ratio: aspectRatioValue,
                duration,
                asset_uuids: assetUuid ? [assetUuid] : [],
            },
        });

        return this.pollAndRegisterMedia(response.data.task_id, 'video_generation', mediaRegistry);
    }
}

const getImageUrlFromAsset = (assetData: AssetContainerModel): string | null => {
    return (
        assetData.background?.image?.preview_url ||
        assetData.background?.image?.thumbnail_url ||
        assetData.background?.video?.poster_url ||
        null
    );
};

const getVideoUrlFromAsset = (assetData: AssetContainerModel): string | null => {
    return (
        assetData.background?.video?.preview_url ||
        assetData.background?.video?.thumbnail_url ||
        null
    );
};

const isVideoAsset = (assetData: AssetContainerModel): boolean => {
    return !!assetData.background?.video;
};

const AIMediaResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const mediaRegistry = MediaRegistry.getInstance();

    let parsedResult;
    try {
        parsedResult = JSON.parse(result);
    } catch (error) {
        return <div>{result}</div>;
    }

    const representations = parsedResult || [];
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const prompt = args.prompt || 'Unknown prompt';
    const referenceMediaId = args.reference_media_id;
    const mediaType = args.media_type || 'image';

    // Get reference image data if provided
    const referenceMediaData = referenceMediaId ? mediaRegistry.getItem(referenceMediaId) : null;
    const referenceImageUrl = referenceMediaData ? getImageUrlFromAsset(referenceMediaData) : null;

    // Determine media type label based on what was actually generated
    const mediaTypeLabel = mediaType === 'video' ? 'video' : 'image';

    return (
        <div className={styles['ai-media-result-container']}>
            {referenceMediaId && referenceImageUrl && (
                <div className="mb-l5-12">
                    <div className="mb-l5-8">
                        <strong>Reference Image:</strong>
                    </div>
                    <div
                        className={`border rounded ${styles['ai-media-image-wrapper']}`}
                        style={{ maxWidth: '300px' }}
                    >
                        <img
                            src={referenceImageUrl}
                            alt="Reference"
                            className={styles['ai-media-image']}
                        />
                    </div>
                </div>
            )}
            <div className="mb-l5-12">
                Generated {representations.length}{' '}
                {pluralize(representations.length, mediaTypeLabel)}{' '}
                {referenceMediaId ? 'from reference' : 'for'} &quot;{prompt}&quot;
            </div>
            <div className="container-fluid">
                <div className="row g-2">
                    {representations.map((rep: MediaRepresentation, index: number) => {
                        try {
                            const fullData = mediaRegistry.getItem(rep.id);

                            if (!fullData) {
                                return (
                                    <div key={index} className="col-md-6">
                                        <div className="border p-l5-8">Missing data: {rep.id}</div>
                                    </div>
                                );
                            }

                            // Check if this is a video asset
                            if (isVideoAsset(fullData)) {
                                const videoUrl = getVideoUrlFromAsset(fullData);
                                if (videoUrl) {
                                    return (
                                        <div key={index} className="col-12">
                                            <div className="border rounded ai-media-video-wrapper">
                                                <video
                                                    src={videoUrl}
                                                    controls
                                                    className="ai-media-video"
                                                    style={{ width: '100%', maxHeight: '400px' }}
                                                />
                                            </div>
                                        </div>
                                    );
                                }
                            }

                            const imageUrl = getImageUrlFromAsset(fullData);

                            if (imageUrl) {
                                return (
                                    <div key={index} className="col-md-6">
                                        <div
                                            className={`border rounded ${styles['ai-media-image-wrapper']}`}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt={rep.title}
                                                className={styles['ai-media-image']}
                                            />
                                        </div>
                                    </div>
                                );
                            }

                            // Fallback: Try BackgroundAsset component
                            const immutableAsset = ImmutableFromJS(fullData);
                            return (
                                <BackgroundAsset
                                    key={index}
                                    searchContext={null}
                                    media={immutableAsset}
                                    media_id={index}
                                    result_index={index}
                                />
                            );
                        } catch (error) {
                            return (
                                <div key={index} className="col-md-6">
                                    <div className="border p-l5-8 bg-danger text-white">
                                        Error: {String(error)}
                                    </div>
                                </div>
                            );
                        }
                    })}
                </div>
            </div>
        </div>
    );
};
